import { Lead } from '../types';

export interface FormVariable {
  name: string;
  label: string;
  fieldKey: keyof Lead | 'todaysDate';
  description?: string;
}

export interface FormVariableGroup {
  group: string;
  vars: FormVariable[];
}

/**
 * Complete group definition of Form Variables corresponding strictly to
 * every field on the Lead Details Form.
 */
export const FORM_VARIABLE_GROUPS: FormVariableGroup[] = [
  {
    group: 'Lead & Contact Details',
    vars: [
      { name: 'Name', label: 'Lead / Contact Name', fieldKey: 'name' },
      { name: 'Company', label: 'Company / Lead Name', fieldKey: 'company' },
      { name: 'CompanyName', label: 'Company Name (Alias)', fieldKey: 'company' },
      { name: 'ContactPerson', label: 'Contact Person Name', fieldKey: 'contactPerson' },
      { name: 'Email', label: 'Lead Email', fieldKey: 'email' },
      { name: 'Phone', label: 'Phone Number', fieldKey: 'phone' },
      { name: 'PhoneNumber', label: 'Phone Number (Alias)', fieldKey: 'phone' },
      { name: 'Owner', label: 'Assigned Sales Rep', fieldKey: 'owner' },
      { name: 'AccountNumber', label: 'Account Number', fieldKey: 'accountNumber' },
      { name: 'Location', label: 'Location (City / Region)', fieldKey: 'location' },
      { name: 'LeadType', label: 'Lead Type', fieldKey: 'leadType' },
    ],
  },
  {
    group: 'Pipeline & Status',
    vars: [
      { name: 'Pipeline', label: 'Pipeline Category', fieldKey: 'pipeline' },
      { name: 'Stage', label: 'Pipeline Stage (Alias)', fieldKey: 'pipeline' },
      { name: 'Status', label: 'Lead Status', fieldKey: 'status' },
      { name: 'FollowUpDate', label: 'Follow-Up Date', fieldKey: 'followUpDate' },
      { name: 'NextSteps', label: 'Next Steps / Action Items', fieldKey: 'nextSteps' },
      { name: 'QuickNotes', label: 'Quick Notes & Call Logs', fieldKey: 'quickNotes' },
      { name: 'AppLink', label: 'Credit App Referral URL', fieldKey: 'appLink' },
      { name: 'CreditAppLink', label: 'Credit App Link (Alias)', fieldKey: 'appLink' },
    ],
  },
  {
    group: 'Operational & Delivery Address',
    vars: [
      { name: 'ConsigneeName', label: 'Consignee Name', fieldKey: 'consigneeName' },
      { name: 'ShipperName', label: 'Shipper Name', fieldKey: 'shipperName' },
      { name: 'Street', label: 'Street Address', fieldKey: 'street' },
      { name: 'City', label: 'City', fieldKey: 'city' },
      { name: 'State', label: 'State', fieldKey: 'state' },
      { name: 'ZipCode', label: 'Zip Code', fieldKey: 'zipCode' },
      { name: 'FullDeliveryView', label: 'Full Delivery Summary', fieldKey: 'fullDeliveryView' },
      { name: 'RoutingOpsApproved', label: 'Routing Ops Approved (Yes/No)', fieldKey: 'routingOpsApproved' },
    ],
  },
  {
    group: 'Multi-Select Selections',
    vars: [
      { name: 'Service', label: 'Primary Service Type', fieldKey: 'service' },
      { name: 'ServiceType', label: 'Primary Service (Alias)', fieldKey: 'service' },
      { name: 'Industry', label: 'Industry Sector', fieldKey: 'industry' },
      { name: 'ServiceTypes', label: 'Selected Service Types', fieldKey: 'serviceTypes' },
      { name: 'Industries', label: 'Selected Industries', fieldKey: 'industries' },
      { name: 'OutboundStates', label: 'Outbound States (FL, CA)', fieldKey: 'outboundStates' },
      { name: 'OutboundWarehouses', label: 'Outbound Warehouses', fieldKey: 'outboundWarehouses' },
      { name: 'DaysOfService', label: 'Scheduled Days of Service', fieldKey: 'daysOfService' },
    ],
  },
  {
    group: 'Quoting Engine & Pricing',
    vars: [
      { name: 'DealValue', label: 'Deal Value ($)', fieldKey: 'dealValue' },
      { name: 'QuoteDollarAmount', label: 'Quote Dollar Amount ($)', fieldKey: 'quoteDollarAmount' },
      { name: 'UOM', label: 'Unit of Measure (UOM)', fieldKey: 'uom' },
      { name: 'HandlingFeeAmount', label: 'Handling Fee Amount ($)', fieldKey: 'handlingFeeAmount' },
      { name: 'NoHandlingFee', label: 'No Handling Fee Flag', fieldKey: 'noHandlingFee' },
      { name: 'FuelSurcharge', label: 'Fuel Surcharge Flag', fieldKey: 'fuelSurcharge' },
      { name: 'QuoteFrequency', label: 'Quote Frequency', fieldKey: 'quoteFrequency' },
      { name: 'QuoteDetails', label: 'Rate Quote Breakdown', fieldKey: 'quoteDetails' },
      { name: 'FullQuoteView', label: 'Full Quote View Summary', fieldKey: 'fullQuoteView' },
      { name: 'EstimatedDealValue', label: 'Calculated Est. Deal Value ($)', fieldKey: 'estimatedDealValue' },
    ],
  },
  {
    group: 'Approvals & Audit Trail',
    vars: [
      { name: 'QuoteRequestedBy', label: 'Quote Requested By', fieldKey: 'quoteRequestedBy' },
      { name: 'QuoteRequestedAt', label: 'Quote Requested At', fieldKey: 'quoteRequestedAt' },
      { name: 'RoutingApprovedBy', label: 'Routing Approved By', fieldKey: 'routingApprovedBy' },
      { name: 'RoutingApprovedAt', label: 'Routing Approved At', fieldKey: 'routingApprovedAt' },
    ],
  },
  {
    group: 'Milestone Dates',
    vars: [
      { name: 'InquiryDate', label: 'Inquiry Milestone Date', fieldKey: 'inquiryDate' },
      { name: 'ProposalDate', label: 'Proposal Milestone Date', fieldKey: 'proposalDate' },
      { name: 'AcceptedDate', label: 'Accepted Milestone Date', fieldKey: 'acceptedDate' },
      { name: 'AccountSetupDate', label: 'Account Setup Date', fieldKey: 'accountSetupDate' },
      { name: 'FirstShipmentDate', label: 'First Shipment Date', fieldKey: 'firstShipmentDate' },
    ],
  },
  {
    group: 'User & Rep Profile',
    vars: [
      { name: 'User Signature', label: 'User CRM Email Signature', fieldKey: 'owner' as any },
      { name: 'UserSignature', label: 'User CRM Email Signature (Alias)', fieldKey: 'owner' as any },
      { name: 'User Name', label: 'Logged-In User Name', fieldKey: 'owner' as any },
      { name: 'User Email', label: 'Logged-In User Email', fieldKey: 'owner' as any },
    ],
  },
  {
    group: 'Disqualification & System',
    vars: [
      { name: 'IsTransferred', label: 'Transferred (Yes/No)', fieldKey: 'isTransferred' },
      { name: 'IsDeclined', label: 'Declined (Yes/No)', fieldKey: 'isDeclined' },
      { name: 'IsNotAwarded', label: 'Not Awarded (Yes/No)', fieldKey: 'isNotAwarded' },
      { name: 'NotAwardedReason', label: 'Not Awarded Reason', fieldKey: 'notAwardedReason' },
      { name: 'TodaysDate', label: "Current Today's Date", fieldKey: 'todaysDate' },
    ],
  },
];

/**
 * Extracts a map of variable name -> string value for a given Lead object and optional User Profile.
 */
export function getLeadVariableMap(
  lead?: Partial<Lead> | null,
  userProfile?: { displayName?: string; email?: string; signature?: string } | null
): Record<string, string> {
  const map: Record<string, string> = {
    TodaysDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    Today: new Date().toISOString().slice(0, 10),
  };

  const localSig = typeof localStorage !== 'undefined' ? localStorage.getItem('crm_user_signature') || '' : '';
  const userSig = userProfile?.signature || localSig;
  map['User Signature'] = userSig;
  map['UserSignature'] = userSig;
  map['User_Signature'] = userSig;
  map['CRMEmailSignature'] = userSig;
  map['User Name'] = userProfile?.displayName || (lead && lead.owner) || '';
  map['UserName'] = map['User Name'];
  map['User Email'] = userProfile?.email || '';
  map['UserEmail'] = map['User Email'];

  if (!lead) return map;

  const formatVal = (val: any): string => {
    if (val === undefined || val === null) return '';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'number') return val.toLocaleString();
    if (Array.isArray(val)) return val.join(', ');
    return String(val);
  };

  // Lead Core & Contact Details
  map['Name'] = lead.name || lead.contactPerson || '';
  map['LeadName'] = lead.name || '';
  map['Company'] = lead.company || '';
  map['CompanyName'] = lead.company || '';
  map['ContactPerson'] = lead.contactPerson || lead.name || '';
  map['ContactName'] = lead.contactPerson || lead.name || '';
  map['Email'] = lead.email || '';
  map['LeadEmail'] = lead.email || '';
  map['Phone'] = lead.phone || '';
  map['PhoneNumber'] = lead.phone || '';
  map['Owner'] = lead.owner || '';
  map['SalesRep'] = lead.owner || '';
  map['AssignedOwner'] = lead.owner || '';
  map['AccountNumber'] = lead.accountNumber || '';
  map['AccountNo'] = lead.accountNumber || '';
  map['BillToAccountNumber'] = lead.billToAccountNumber || lead.accountNumber || '';
  map['CareOf'] = lead.careOf || '';
  map['Location'] = lead.location || '';
  map['CityRegion'] = lead.location || '';
  map['LeadType'] = formatVal(lead.leadType);
  map['LeadTypes'] = formatVal(lead.leadType);

  // Pipeline & Status
  map['Pipeline'] = lead.pipeline || '';
  map['Stage'] = lead.pipeline || '';
  map['PipelineCategory'] = lead.pipeline || '';
  map['Status'] = lead.status || '';
  map['LeadStatus'] = lead.status || '';
  map['FollowUpDate'] = lead.followUpDate || '';
  map['FollowUp'] = lead.followUpDate || '';
  map['LeadNotes'] = lead.leadNotes || lead.nextSteps || '';
  map['NextSteps'] = lead.leadNotes || lead.nextSteps || '';
  map['QuickNotes'] = lead.quickNotes || '';
  map['FollowUpNotes'] = lead.followUpNotes || '';
  map['Notes'] = lead.leadNotes || lead.followUpNotes || lead.quickNotes || '';
  map['AppLink'] = lead.appLink || '';
  map['CreditAppLink'] = lead.appLink || '';

  // Operational & Address
  map['ConsigneeName'] = lead.consigneeName || '';
  map['consigneename'] = lead.consigneeName || '';
  map['ShipperName'] = lead.shipperName || '';
  map['shippername'] = lead.shipperName || '';
  map['Street'] = lead.street || '';
  map['StreetAddress'] = lead.street || '';
  map['City'] = lead.city || '';
  map['State'] = lead.state || '';
  map['ZipCode'] = lead.zipCode || '';
  map['Zip_Code'] = lead.zipCode || '';
  map['FullDeliveryView'] = lead.fullDeliveryView || '';
  map['DeliveryDirections'] = lead.fullDeliveryView || '';
  map['RoutingOpsApproved'] = formatVal(lead.routingOpsApproved);
  map['OpsApproved'] = formatVal(lead.routingOpsApproved);

  // Multi-Selects
  map['Service'] = lead.service || (lead.serviceTypes && lead.serviceTypes.join(', ')) || '';
  map['ServiceType'] = lead.service || (lead.serviceTypes && lead.serviceTypes.join(', ')) || '';
  map['Industry'] = lead.industry || (lead.industries && lead.industries.join(', ')) || '';
  map['IndustrySector'] = lead.industry || (lead.industries && lead.industries.join(', ')) || '';
  map['ServiceTypes'] = formatVal(lead.serviceTypes);
  map['Industries'] = formatVal(lead.industries);
  map['OutboundStates'] = formatVal(lead.outboundStates);
  map['OutboundWarehouses'] = formatVal(lead.outboundWarehouses);
  map['DaysOfService'] = formatVal(lead.daysOfService);
  map['DOS'] = formatVal(lead.daysOfService);

  // Quoting & Pricing
  map['DealValue'] = lead.dealValue ? `$${lead.dealValue.toLocaleString()}` : '$0';
  map['EstimatedValue'] = lead.dealValue ? `$${lead.dealValue.toLocaleString()}` : '$0';
  map['QuoteDollarAmount'] = lead.quoteDollarAmount ? `$${lead.quoteDollarAmount.toLocaleString()}` : '$0';
  map['UOM'] = lead.uom || '';
  map['Uom'] = lead.uom || '';
  map['UnitOfMeasure'] = lead.uom || '';
  map['HandlingFeeAmount'] = lead.handlingFeeAmount ? `$${lead.handlingFeeAmount.toLocaleString()}` : '$0';
  map['NoHandlingFee'] = formatVal(lead.noHandlingFee);
  map['FuelSurcharge'] = formatVal(lead.fuelSurcharge);
  map['FSC'] = formatVal(lead.fuelSurcharge);
  map['QuoteFrequency'] = lead.quoteFrequency || '';
  map['Frequency'] = lead.quoteFrequency || '';

  // Handle quoteDetails JSON or plain string
  let parsedQuote = lead.quoteDetails || '';
  if (parsedQuote.trim().startsWith('{')) {
    try {
      const obj = JSON.parse(parsedQuote);
      parsedQuote = obj.text || parsedQuote;
    } catch {
      /* ignore */
    }
  }
  map['QuoteDetails'] = parsedQuote;
  map['RateDetails'] = parsedQuote;
  map['FullQuoteView'] = lead.fullQuoteView || parsedQuote;
  map['QuoteSummary'] = lead.fullQuoteView || parsedQuote;
  map['EstimatedDealValue'] = lead.estimatedDealValue ? `$${lead.estimatedDealValue.toLocaleString()}` : '$0';

  // Approvals
  map['QuoteRequestedBy'] = lead.quoteRequestedBy || '';
  map['QuoteRequestedAt'] = lead.quoteRequestedAt || '';
  map['RoutingApprovedBy'] = lead.routingApprovedBy || '';
  map['RoutingApprovedAt'] = lead.routingApprovedAt || '';

  // Milestone Dates
  map['InquiryDate'] = lead.inquiryDate || '';
  map['ProposalDate'] = lead.proposalDate || '';
  map['AcceptedDate'] = lead.acceptedDate || '';
  map['AccountSetupDate'] = lead.accountSetupDate || '';
  map['FirstShipmentDate'] = lead.firstShipmentDate || '';

  // Disqualifiers
  map['IsTransferred'] = formatVal(lead.isTransferred);
  map['IsDeclined'] = formatVal(lead.isDeclined);
  map['IsNotAwarded'] = formatVal(lead.isNotAwarded);
  map['NotAwardedReason'] = lead.notAwardedReason || '';

  return map;
}

/**
 * Replaces {{VariableName}} placeholders in content string with values from the given lead and optional userProfile.
 */
export function fillFormTemplate(
  templateContent: string,
  lead?: Partial<Lead> | null,
  userProfile?: { displayName?: string; email?: string; signature?: string } | null
): string {
  if (!templateContent) return '';
  const map = getLeadVariableMap(lead, userProfile);

  return templateContent.replace(/\{\{\s*([a-zA-Z0-9_\s]+)\s*\}\}/g, (match, varName) => {
    const trimmed = varName.trim();
    if (map[trimmed] !== undefined) {
      return map[trimmed];
    }
    const lowerKey = trimmed.toLowerCase();
    const foundKey = Object.keys(map).find((k) => k.toLowerCase() === lowerKey);
    if (foundKey && map[foundKey] !== undefined) {
      return map[foundKey];
    }
    return match; // Leave unfulfilled placeholders as-is
  });
}
